
import {
  Datagrid,
  DeleteButton,
  EditButton,
  FunctionField,
  List,
  TextField,
  useGetList,
  useGetOne,
  useRecordContext,
  useShowController,
  useTranslate,
} from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components';
import { Resources } from '../../resource';
import { Box, Card, Chip, Typography, Link } from '@mui/material';
import { set } from 'lodash';
import { on } from 'events';
import dayjs from 'dayjs';
import { TIME_FORMAT } from 'src/configs/common.config';
import CopyBtn from 'src/components/button/CopyBtn';
import ErrorPage403 from 'src/pages/error/403';
import { FiExternalLink } from 'react-icons/fi';

const TX_STATUS_COLOR: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  Success: 'success',
  Failed: 'error',
  Pending: 'warning',
  Unknown: 'warning',
  Expired: 'default',
};

const View = () => {
  const t = useTranslate();

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <PermissionGuard resource="withdraw" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs heading={t('resource.withdraw')} />
        <Card sx={{ mt: 3, pb: 2, paddingTop: 2 }}>
          <List exporter={false} actions={false} empty={false} perPage={10}>
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                sx={{
                  '& .RaDatagrid-row': {
                    borderBottom: '12px solid important',
                  },
                }}
              >
                <FunctionField
                  label={t('common.from')}
                  render={(record: any) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Typography>{`${record.signWith.slice(0, 6)}.....${record.signWith.slice(-4)}`}</Typography>
                      <CopyBtn text={record.signWith} showMsg={false} />
                    </Box>
                  )}
                  sortable={false}
                />{' '}
                <FunctionField
                  label={t('common.to')}
                  render={(record: any) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Typography>{`${record.toAddress.slice(0, 6)}.....${record.toAddress.slice(-4)}`}</Typography>
                      <CopyBtn text={record.toAddress} showMsg={false} />{' '}
                    </Box>
                  )}
                  sortable={false}
                />{' '}
                <TextField
                  source="chain"
                  label={t('module.tokenMetadata.chain')}
                  sortable={false}
                />
                <FunctionField
                  label={t('module.transaction.amount')}
                  render={(record) => (
                    <span style={{ whiteSpace: 'nowrap' }}>
                      {record.amount} {record.symbol}
                    </span>
                  )}
                />
                <FunctionField label={t('TxHash')} render={renderTxHashLink} sortable={false} />
                <FunctionField
                  label={t('module.financeTransfers.requestId', { _: 'Request' })}
                  render={(record: any) => (record.requestId ? `${String(record.requestId).slice(0, 8)}...` : '')}
                  sortable={false}
                />
                <FunctionField
                  label="module.withdrawal.transactionStatus"
                  render={(record) => {
                    if (!record.transactionStatus) return null;
                    return (
                      <Chip
                        label={record.transactionStatus}
                        color={TX_STATUS_COLOR[record.transactionStatus] ?? 'default'}
                        size="small"
                      />
                    );
                  }}
                  sortable={false}
                />
                <FunctionField
                  source="createdAt"
                  label="module.transaction.createdAt"
                  minWidth={180}
                  maxWidth={200}
                  render={(record) =>
                    record.createdAt ? dayjs(record.createdAt).format(TIME_FORMAT) : ''
                  }
                />
              </Datagrid>
            </Box>
          </List>
        </Card>
      </DashboardContent>
    </PermissionGuard>
  );
};
const renderTxHashLink = (record: any) => {
  if (!record.txHash) return '';
  const shortHash = `${record.txHash.slice(0, 6)}...${record.txHash.slice(-4)}`;
  const explorerUrls: Record<string, string> = {
    ETH: 'https://etherscan.io/tx/',
    ARB: 'https://arbiscan.io/tx/',
    BSC: 'https://bscscan.com/tx/',
    SOLANA: 'https://solscan.io/tx/',
  };
  const baseUrl = explorerUrls[record.chain] || 'https://etherscan.io/tx/';
  const url = `${baseUrl}${record.txHash}`;

  return (
    <Link
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        textDecoration: 'none',
      }}
    >
      {shortHash}
      <FiExternalLink size={16} />
    </Link>
  );
};

export default View;
